/**
 * 内容仓地址的解析与浏览器持久化。
 *
 * 只接受公开的 gitee.com 仓库；地址本身不是密钥，所以放在 localStorage。
 * 首次连接成功后写入，之后启动优先读它；解析失败不会覆盖已有配置。
 */

export interface RepoAddress {
  readonly owner: string
  readonly repo: string
  /** 规范化后的网页地址，例如 https://gitee.com/owner/repo */
  readonly url: string
}

export interface RepoConfig extends RepoAddress {
  readonly ref: string
  readonly bundlePath: string
  /** 第一次成功连接并写入 localStorage 的时间。 */
  readonly savedAt: number
}

export interface RepoDraft {
  readonly url: string
  readonly ref: string
  readonly bundlePath: string
}

export type RepoResult<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: string }

export const REPO_STORAGE_KEY = 'kc.repo-config.v1'
export const DEFAULT_REF = 'master'
export const DEFAULT_BUNDLE = 'dist/content.json'

const NAME = /^[A-Za-z0-9][A-Za-z0-9_.-]*$/

function fail(message: string): RepoResult<never> {
  return { ok: false, error: message }
}

function ok<T>(value: T): RepoResult<T> {
  return { ok: true, value }
}

function normalizeParts(owner: string, repo: string): RepoResult<RepoAddress> {
  const cleanOwner = owner.trim()
  const cleanRepo = repo.trim().replace(/\.git$/i, '')
  if (!NAME.test(cleanOwner) || !NAME.test(cleanRepo)) {
    return fail('仓库地址里的 owner/repo 只能包含字母、数字、点、短横线或下划线。')
  }
  if (cleanOwner === '.' || cleanOwner === '..' || cleanRepo === '.' || cleanRepo === '..') {
    return fail('仓库地址不合法。')
  }
  return ok({
    owner: cleanOwner,
    repo: cleanRepo,
    url: `https://gitee.com/${cleanOwner}/${cleanRepo}`,
  })
}

/**
 * 支持四种写法：
 * - https://gitee.com/owner/repo
 * - https://gitee.com/owner/repo.git
 * - git@gitee.com:owner/repo.git
 * - owner/repo
 */
export function parseRepoAddress(input: string): RepoResult<RepoAddress> {
  const text = input.trim()
  if (text.length === 0) return fail('请填写仓库地址。')

  const ssh = /^git@gitee\.com:([^/]+)\/([^/]+)$/i.exec(text)
  if (ssh !== null) return normalizeParts(ssh[1] as string, ssh[2] as string)

  if (/^https?:\/\//i.test(text)) {
    let url: URL
    try {
      url = new URL(text)
    } catch {
      return fail('仓库地址不是合法 URL。')
    }
    if (url.hostname.toLowerCase() !== 'gitee.com' && url.hostname.toLowerCase() !== 'www.gitee.com') {
      return fail('目前只支持 gitee.com 的公开仓库。')
    }
    const parts = url.pathname.split('/').filter((part) => part.length > 0)
    if (parts.length !== 2) {
      return fail('地址应指向仓库根，例如 https://gitee.com/owner/repo。')
    }
    return normalizeParts(parts[0] as string, parts[1] as string)
  }

  const short = /^([^/]+)\/([^/]+)$/.exec(text)
  if (short !== null) return normalizeParts(short[1] as string, short[2] as string)

  return fail('仓库地址格式不对。示例：https://gitee.com/owner/repo。')
}

function normalizeRef(input: string): RepoResult<string> {
  const ref = input.trim() || DEFAULT_REF
  if (
    ref.length === 0 ||
    /\s/.test(ref) ||
    ref.includes('..') ||
    /[~^:?*[\\]/.test(ref) ||
    ref.startsWith('/') ||
    ref.endsWith('/') ||
    ref.startsWith('.')
  ) {
    return fail('分支名不合法。通常填 master 或 main。')
  }
  return ok(ref)
}

function normalizeBundle(input: string): RepoResult<string> {
  const bundle = (input.trim() || DEFAULT_BUNDLE).replace(/^\/+/, '')
  const parts = bundle.split('/')
  if (!bundle.endsWith('.json') || parts.some((part) => part === '..' || part.length === 0)) {
    return fail('内容包路径必须是仓库内的相对 .json 文件，例如 dist/content.json。')
  }
  return ok(bundle)
}

export function resolveRepoConfig(draft: RepoDraft, savedAt = Date.now()): RepoResult<RepoConfig> {
  const address = parseRepoAddress(draft.url)
  if (!address.ok) return address

  const ref = normalizeRef(draft.ref)
  if (!ref.ok) return ref

  const bundle = normalizeBundle(draft.bundlePath)
  if (!bundle.ok) return bundle

  return ok({
    ...address.value,
    ref: ref.value,
    bundlePath: bundle.value,
    savedAt,
  })
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export function loadRepoConfig(): RepoConfig | null {
  const store = storage()
  if (store === null) return null

  let raw: string | null
  try {
    raw = store.getItem(REPO_STORAGE_KEY)
  } catch {
    return null
  }
  if (raw === null) return null

  try {
    const parsed = JSON.parse(raw) as {
      v?: unknown
      url?: unknown
      ref?: unknown
      bundlePath?: unknown
      savedAt?: unknown
    }
    if (parsed.v !== 1 || typeof parsed.url !== 'string' || typeof parsed.ref !== 'string' || typeof parsed.bundlePath !== 'string') {
      return null
    }
    const savedAt = typeof parsed.savedAt === 'number' && Number.isFinite(parsed.savedAt) ? parsed.savedAt : Date.now()
    const resolved = resolveRepoConfig(
      {
        url: parsed.url,
        ref: parsed.ref,
        bundlePath: parsed.bundlePath,
      },
      savedAt,
    )
    return resolved.ok ? resolved.value : null
  } catch {
    return null
  }
}

export function saveRepoConfig(config: RepoConfig): RepoConfig {
  const store = storage()
  if (store === null) return config

  const savedAt = Date.now()
  const value: RepoConfig = { ...config, savedAt }
  try {
    store.setItem(
      REPO_STORAGE_KEY,
      JSON.stringify({
        v: 1,
        url: value.url,
        ref: value.ref,
        bundlePath: value.bundlePath,
        savedAt,
      }),
    )
  } catch {
    // 隐私模式/配额满时仍然返回配置，本次会话可用，只是下次要重填。
  }
  return value
}

export function clearRepoConfig(): void {
  try {
    storage()?.removeItem(REPO_STORAGE_KEY)
  } catch {
    // 忽略。
  }
}
